import React, { useEffect, useState } from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Legend,
    CartesianGrid
} from 'recharts';

const colors = [
    '#1E40AF', '#67E8F9', '#A21CAF', '#7C3AED', '#F59E42',
    '#F43F5E', '#22D3EE', '#FBBF24', '#6366F1', '#EF4444'
];

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div style={{ 
                background: '#ffffff', 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px',
                padding: '12px 16px', 
                minWidth: '200px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}>
                <div style={{ 
                    fontWeight: 600, 
                    marginBottom: '8px', 
                    color: '#1f2937',
                    fontSize: '14px'
                }}>
                    Trade #{label}
                </div>
                {payload
                    .filter((entry: any) => entry.dataKey !== 'avg_equity')
                    .map((entry: any, idx: number) => (
                        <div key={entry.dataKey} style={{ 
                            color: entry.stroke, 
                            fontWeight: 500,
                            fontSize: '13px',
                            marginBottom: '4px'
                        }}>
                            Curve {idx + 1}: ${entry.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                        </div>
                    ))}
                <div style={{ 
                    color: '#000', 
                    fontWeight: 700, 
                    marginTop: '8px',
                    paddingTop: '8px',
                    borderTop: '1px solid #e5e7eb',
                    fontSize: '14px'
                }}>
                    Average: ${payload.find((e: any) => e.dataKey === 'avg_equity')?.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                </div>
            </div>
        );
    }
    return null;
};

interface EquityCurveProps {
    avg_equity: number;
    line1: number;
    line2: number;
    line3: number;
    line4: number;
    line5: number;
    line6: number;
    line7: number;
    line8: number;
    line9: number;
    line10: number;
    trade: number;
}

interface TradingChartProps {
    equityCurves: EquityCurveProps[];
    avgDataTable: {
        avg_drawdown_percent: number;
        max_consecutive_losers: number;
        max_consecutive_winners: number;
        max_drawdown_percent: number;
        max_equity: number;
        min_equity: number;
    };
}

const TradingChart: React.FC<TradingChartProps> = ({ equityCurves, avgDataTable }) => {
    const [betweenMinMax, setBetweenMinMax] = useState<number[]>([]);

    const handleDistanceBetweenChart = () => {
        const filData = [];
        const range = avgDataTable.max_equity - avgDataTable.min_equity;
        const step = range / 9; // 10 ticks total
        
        for (let i = 0; i < 10; i++) {
            const data = avgDataTable.min_equity + (step * i);
            const handledData = Number(data.toFixed(2));
            filData.push(handledData);
        }
        setBetweenMinMax(filData);
    };

    useEffect(() => {
        if (avgDataTable && equityCurves.length > 0) {
            handleDistanceBetweenChart();
        }
    }, [avgDataTable, equityCurves]);

    if (!equityCurves || equityCurves.length === 0) {
        return (
            <div className="flex items-center justify-center h-[350px] bg-gray-50 rounded-lg">
                <div className="text-center">
                    <div className="text-gray-500 text-lg mb-2">📊</div>
                    <p className="text-gray-600">No equity curve data available</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full">
            <div className="rounded-lg p-4 shadow-sm border border-gray-100" style={{
                backgroundColor: localStorage.getItem('theme') === 'dark' ? '#1F2937' : '#FFFFFF',
                borderColor: '#F3F4F6'
            }}>
                <ResponsiveContainer width="100%" height={350}>
                    <LineChart
                        data={equityCurves}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                        <XAxis
                            dataKey="trade"
                            type="number"
                            domain={[0, equityCurves.length]}
                            ticks={Array.from({ length: Math.min(6, equityCurves.length) }, (_, i) => 
                                Math.floor((i * equityCurves.length) / (Math.min(6, equityCurves.length) - 1))
                            )}
                            tick={{ fontSize: 12, fill: '#6b7280' }}
                            tickLine={false}
                            axisLine={false}
                            label={{
                                value: 'Number of Trades',
                                position: 'insideBottom',
                                offset: -5,
                                style: { textAnchor: 'middle', fontSize: 12, fill: '#6b7280' }
                            }}
                        />
                        {betweenMinMax.length > 0 && (
                            <YAxis
                                domain={[avgDataTable.min_equity, avgDataTable.max_equity]}
                                ticks={betweenMinMax}
                                tickFormatter={val => `$${val.toLocaleString()}`}
                                tick={{ fontSize: 12, fill: '#6b7280' }}
                                tickLine={false}
                                axisLine={false}
                                label={{
                                    value: '',
                                    angle: -90,
                                    position: 'insideLeft',
                                    style: { textAnchor: 'middle', fontSize: 12, fill: '#6b7280' }
                                }}
                            />
                        )}
                        <Tooltip content={<CustomTooltip />} />
                        
                        {/* 10 simulation lines */}
                        {Array.from({ length: 10 }).map((_, idx) => (
                            <Line
                                key={`line_${idx + 1}`}
                                type="linear"
                                dataKey={`line${idx + 1}`}
                                stroke={colors[idx]}
                                strokeWidth={1.5}
                                dot={false}
                                activeDot={{ r: 4, stroke: colors[idx], strokeWidth: 2 }}
                                isAnimationActive={true}
                            />
                        ))}
                        
                        {/* Average line - black and bold */}
                        <Line
                            type="linear"
                            dataKey="avg_equity"
                            stroke="#1f2937"
                            strokeWidth={3}
                            dot={false}
                            activeDot={{ r: 5, stroke: '#1f2937', strokeWidth: 3 }}
                            isAnimationActive={true}
                        />
                    </LineChart>
                </ResponsiveContainer>
                
                <div className="mt-4 text-center">
                    <p className="text-sm text-gray-600">
                        The <span className="font-semibold text-gray-900">black line</span> represents the average of all simulation curves.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default TradingChart;